import { alpha, createTheme, getContrastRatio, lighten } from '@mui/material/styles';
import { glassBar, glassSurface, makeGlass } from './glass';

/**
 * The app-wide MUI theme (brand dashboard + platform admin).
 *
 * Built by a factory so the dashboard can take on the colour of whichever
 * brand is selected. `makeTheme()` with no argument gives the platform's own
 * palette, which is what the landing page, the auth screens and any brand
 * that has not picked a colour get.
 *
 * `mode` is 'light' or 'dark'. It is a second axis, not a second theme: the
 * brand still picks the hue, the mode picks the ground it sits on, and every
 * surface token in styles/glass.js has a value for each.
 *
 * Floating surfaces are glassed here, at the theme level, rather than in each
 * of the ~40 dialog files. Anything rendering a plain <Dialog>, <Menu>,
 * <Popover> or <Drawer> picks the treatment up for free, and a component that
 * genuinely needs something else can still override via `sx` (which wins on
 * specificity).
 */
// The platform's own colour, and the fallback for a brand with none set.
export const DEFAULT_PRIMARY = '#422AFB';

/** A 6-digit hex, or null. Anything else is not worth repainting an app over. */
const safeColor = (c) => (/^#[0-9a-fA-F]{6}$/.test(String(c || '')) ? c : null);

// The dark grounds. Two steps, not one: the page is darker than the cards on
// it, so a card still reads as a raised surface without needing a border.
const DARK_PAGE = '#0B0F17';
const DARK_PAPER = '#161C27';

/**
 * A brand colour deep enough to carry white text on a white page can be
 * nearly invisible as ink on a dark one — the platform's own #422AFB scores
 * about 1.5:1 against #161C27. Filled buttons would survive that, because
 * they supply their own ground; every text button, link, tab label, switch
 * and focus ring made from the same colour would not.
 *
 * So in dark mode the colour is lifted until it reads, and the entire palette
 * moves with it rather than only the places that would have failed: a brand
 * that is pale as a link and deep as a button is two brands, and the page
 * stops looking like it was designed by one person.
 */
const liftForDark = (hex) => {
  let c = hex;
  for (let i = 0; i < 24 && getContrastRatio(c, DARK_PAPER) < 4.5; i += 1) {
    c = lighten(c, 0.06);
  }
  return c;
};

export function makeTheme(brandColor, mode = 'light') {
  const isDark = mode === 'dark';
  const brand = safeColor(brandColor) || DEFAULT_PRIMARY;
  const primary = isDark ? liftForDark(brand) : brand;
  const glass = makeGlass(mode);

  return createTheme({
  // Hung on the theme so components reach the right set through `useGlass()`
  // instead of importing one of them and being stuck in that mode forever.
  glass,
  palette: {
    mode,
    primary: { main: primary },
    secondary: { main: isDark ? lighten('#9333EA', 0.22) : '#9333EA' },
    background: isDark
      ? { default: DARK_PAGE, paper: DARK_PAPER }
      : { default: '#f4f7fe', paper: '#ffffff' },
    ...(isDark
      ? {
          text: {
            primary: '#E6EAF2',
            secondary: 'rgba(230, 234, 242, 0.64)',
            disabled: 'rgba(230, 234, 242, 0.38)',
          },
          divider: 'rgba(255, 255, 255, 0.11)',
        }
      : {}),
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: 'DM Sans, Helvetica, Arial, sans-serif',
  },
  components: {
    /**
     * Tell the browser which mode it is in. Without this the scrollbars, the
     * native date pickers, the autofill wash on a remembered password and the
     * caret all stay in light dress — a white scrollbar down the side of a
     * dark page is the single most obvious tell that a dark mode was painted
     * on rather than switched on.
     */
    MuiCssBaseline: {
      styleOverrides: {
        ':root': { colorScheme: mode },
      },
    },

    // Dim + blur whatever is behind a modal, so the glass has something to
    // refract. `invisible` backdrops MUST stay transparent — Menu, Select and
    // Popover all render one to catch outside clicks, and tinting it would
    // drop a grey sheet over the page every time a dropdown opens.
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&:not(.MuiBackdrop-invisible)': {
            // glass.backdrop is a gradient, so it has to go through
            // background-image — a gradient in background-color is invalid and
            // would silently leave MUI's default flat black scrim in place.
            backgroundColor: 'transparent',
            backgroundImage: glass.backdrop,
            backdropFilter: glass.backdropBlur,
            WebkitBackdropFilter: glass.backdropBlur,
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          ...glassSurface({ glass }),

          // Phones, handled once here rather than per dialog.
          //
          // MUI's default 32px margin costs a sixth of a 390px screen, and
          // `maxHeight: calc(100% - 64px)` leaves a tall form scrolling
          // inside an already-short box. Both are fine on a laptop and wrong
          // on a phone, and the app has ~20 dialogs — setting `fullScreen`
          // on each is a rule nobody remembers for the twenty-first.
          //
          // Not `fullScreen`: that squares off the corners and loses the
          // glass edge the whole surface language is built on. A tight margin
          // keeps the pane reading as a pane while giving back the width.
          '@media (max-width: 599.95px)': {
            margin: 12,
            width: 'calc(100% - 24px)',
            maxWidth: 'calc(100% - 24px)',
            maxHeight: 'calc(100% - 24px)',
          },
        },
      },
    },

    // The title/actions bars used to be painted solid so they'd separate from
    // the body. On glass that reads as a white slab bolted to a translucent
    // panel, so both are transparent now and the separation comes from a
    // hairline instead.
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          ...glassBar(glass),
          fontWeight: 600,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { backgroundColor: 'transparent' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          ...glassBar(glass),
          borderTop: 'none',
          padding: '16px 24px 20px',
          gap: '8px',
        },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: glassSurface({ strong: true, radius: glass.radiusSmall, glass }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: glassSurface({ strong: true, radius: glass.radiusSmall, glass }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        // Every drawer in the app today is temporary — the mobile nav and the
        // admin detail panels — so all of them are popups and all of them get
        // glass. A docked/permanent drawer would also carry these anchor
        // classes, so if one is ever added it needs an explicit opaque
        // override: it is page furniture, and blurring it just smears the
        // content it is docked against.
        paperAnchorRight: glassSurface({ radius: glass.radius, glass }),
        paperAnchorLeft: glassSurface({ radius: glass.radius, glass }),
      },
    },

    // Form fields inside a glass panel. An opaque white input is just another
    // slab sitting on the surface — the panel has to read as one continuous
    // pane. Translucent fill, slightly firmer border so it is still obviously
    // a field, and it solidifies on focus so what you are typing stays crisp.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '.MuiDialog-paper &, .MuiDrawer-paper &': {
            backgroundColor: glass.control,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: glass.controlBorder,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: glass.controlBorderHover,
            },
            '&.Mui-focused': {
              backgroundColor: glass.controlFocus,
            },
          },
        },
      },
    },

    // Buttons ON a glass panel become glass themselves. Scoped to glass
    // surfaces only — a button on a normal page sits on a white card, where
    // a translucent fill would just look washed out.
    //
    // The fill colour is derived from whatever palette colour the button
    // already uses, so a destructive `color="error"` button stays red rather
    // than being repainted brand indigo.
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const paletteColor =
            (ownerState.color && ownerState.color !== 'inherit' && theme.palette[ownerState.color]?.main) ||
            theme.palette.primary.main;

          const filled = ownerState.variant === 'contained';

          return {
            '.MuiDialog-paper &, .MuiDrawer-paper &': {
              backdropFilter: glass.buttonBlur,
              WebkitBackdropFilter: glass.buttonBlur,
              boxShadow: glass.rimSmall,
              ...(filled
                ? {
                    backgroundColor: alpha(paletteColor, glass.buttonFillAlpha),
                    '&:hover': {
                      backgroundColor: alpha(paletteColor, glass.buttonFillAlphaHover),
                      // Brighter rim + a short outer bloom: the pane catches
                      // more light rather than changing colour.
                      boxShadow: `${glass.buttonHoverGlow}, ${glass.buttonHoverRim}`,
                    },
                  }
                : {
                    backgroundColor: glass.buttonQuiet,
                    '&:hover': {
                      backgroundColor: glass.buttonQuietHover,
                      boxShadow: `${glass.buttonHoverGlow}, ${glass.buttonHoverRim}`,
                    },
                  }),
              // Without this a disabled button keeps the fill above and still
              // looks clickable — the Submit button is disabled until the form
              // validates, so this is the state it spends most of its life in.
              // The light goes out: the pane is still visible (you can see
              // its edges) but nothing on it is lit. Note this deliberately
              // avoids fading the label out — a disabled button still has to
              // say what it does. `GlassButton` reproduces this exact look for
              // the soft-disabled case, where the control stays alive so it can
              // explain itself.
              '&.Mui-disabled': {
                backgroundColor: glass.buttonDisabled,
                color: glass.buttonDisabledText,
                boxShadow: glass.buttonDisabledRim,
              },
            },
          };
        },
      },
    },

    // The dialog close X and any other icon button on glass.
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition:
            'background-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
          // Off-glass (toolbars, table rows) there is no pane to light, so the
          // hover is just a faint brand tint and a slight lift. A translucent
          // fill here would look washed out on a white card.
          '&:hover': { transform: 'scale(1.06)' },
          '&:active': { transform: 'scale(0.98)' },

          '.MuiDialog-paper &, .MuiDrawer-paper &': {
            backgroundColor: glass.buttonQuiet,
            backdropFilter: glass.buttonBlur,
            WebkitBackdropFilter: glass.buttonBlur,
            boxShadow: glass.rimSmall,
            // The dialog close X lives here. On hover it lights up: brighter
            // fill, brighter rim, and a bloom under it so it lifts off the
            // panel.
            '&:hover': {
              backgroundColor: glass.buttonQuietHover,
              boxShadow: `${glass.buttonHoverGlow}, ${glass.buttonHoverRim}`,
              transform: 'scale(1.06)',
            },
            '&.Mui-disabled': {
              backgroundColor: glass.buttonDisabled,
              color: glass.buttonDisabledText,
              boxShadow: glass.buttonDisabledRim,
            },
          },
        },
      },
    },

    // Tooltips are the channel that explains *why* a control is disabled, so
    // they get the same glass as every other floating surface. They have no
    // scrim behind them, which puts them in the `strong` bracket alongside
    // menus and popovers.
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          ...glassSurface({ strong: true, radius: glass.radiusSmall, glass }),
          color: glass.tooltipText,
          fontSize: '13px',
          lineHeight: 1.45,
          fontWeight: 500,
          padding: '8px 12px',
          // Long-form copy — "fill in X and Y before saving" — needs to wrap
          // rather than run off the side of the viewport on a phone.
          maxWidth: 280,
        },
        arrow: {
          color: glass.surfaceStrong,
          '&::before': {
            border: glass.border,
            backdropFilter: glass.blurStrong,
            WebkitBackdropFilter: glass.blurStrong,
          },
        },
      },
    },

    // Menu rows need to sit on the glass, not on an opaque hover chip.
    MuiMenuItem: {
      styleOverrides: {
        root: {
          // Derived from the active primary rather than hardcoded, or a brand
          // in green would still get purple selection highlights.
          '&:hover': { backgroundColor: alpha(primary, 0.08) },
          '&.Mui-selected': { backgroundColor: alpha(primary, 0.12) },
          '&.Mui-selected:hover': { backgroundColor: alpha(primary, 0.16) },
        },
      },
    },
  },
  });
}

// Module-level default for anything rendering outside BrandThemeProvider.
const theme = makeTheme(null, 'light');

export default theme;
